import { useCallback } from 'react';
import { resolveSource, applyTransform } from './schemaTransforms';

/**
 * Custom hook for assembling the save payload from schema definitions.
 * Reads formSchema.savePayload to map state/props to the API payload.
 *
 * @param {Object} formSchema - The dynamic form schema
 * @param {Object} props - Props from ManagedProductConfig
 * @param {Object} dynamicState - Dynamic state managed by DynamicConfigForm
 * @returns {{ buildPayload: Function }}
 */
export function useDynamicSavePayload(formSchema, props, dynamicState) {
  /**
   * Builds the data payload for BZAPIService.saveSetting().
   * @param {Object} [secretResults={}] - Results from credential secret saves (keyed by resultKey)
   * @returns {Object} The payload object
   */
  const buildPayload = useCallback(
    (secretResults = {}) => {
      const payloadSchema = formSchema?.savePayload;
      if (!payloadSchema) return {};

      // Start with static fields
      const data = { ...(payloadSchema.staticFields || {}) };

      // Map dynamic fields
      const mapFields = payloadSchema.mapFields || {};
      Object.entries(mapFields).forEach(([payloadKey, config]) => {
        const rawValue = resolveSource(config.source, props, dynamicState, secretResults);
        if (config.transform) {
          data[payloadKey] = applyTransform(config.transform, rawValue, props);
        } else {
          data[payloadKey] = rawValue;
        }
      });

      return data;
    },
    [formSchema, props, dynamicState]
  );

  return { buildPayload };
}
